import { type RefObject, useCallback, useEffect, useRef } from 'react'

import { type Root, createRoot } from 'react-dom/client'

import { useResolvedNodeDefinitions } from 'Automations/automationEditor/atoms'
import { NodeType } from 'Automations/nodeDefinitions/types'
import { getNodeDescription } from 'Automations/nodeDefinitions'
import { DragMorphPreview } from './DragMorphPreview'

// Target dimensions (in canvas pixels) that match each node type's
// default rendered size on the canvas.
const TARGET_DIMENSIONS: Partial<
  Record<NodeType, { width?: number; height?: number }>
> = {
  SWITCH: { width: 315 },
  REVIEW_PLACEHOLDER: { width: 133 },
  NOTE: { width: 222, height: 222 }
}

// Icon size in the toolbar (screen pixels)
const ICON_SIZE = 32

const MORPH_DISTANCE = 150

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

type MorphContext = {
  container: HTMLElement | null
  iconEl: HTMLElement | null
  labelEl: HTMLElement | null
  bodyEl: HTMLElement | null
  progress: number
  padding: number
  scale: number
  cursorX: number
  cursorY: number
  overlay: HTMLElement
  targetWidth?: number
  targetHeight?: number
}

const updateNoteMorph = (ctx: MorphContext) => {
  const {
    container,
    iconEl,
    bodyEl,
    progress,
    padding,
    scale,
    cursorX,
    cursorY,
    overlay,
    targetWidth,
    targetHeight
  } = ctx

  if (container) {
    container.style.position = 'relative'
    container.style.padding = `${padding}px`
    container.style.borderRadius = `${lerp(6, 12, progress)}px`
    container.style.background = `rgba(254, 243, 199, ${progress})`
    container.style.border = `rgba(146, 64, 14, ${progress * 0.16}) 1px solid`
    container.style.boxShadow = 'none'
    container.style.transform = `scale(${scale})`
    container.style.transformOrigin = 'top left'
    if (targetWidth) {
      container.style.width = `${lerp(ICON_SIZE, targetWidth, progress)}px`
    }
    if (targetHeight) {
      container.style.minHeight = `${lerp(ICON_SIZE, targetHeight, progress)}px`
    }
  }

  if (iconEl) {
    const iconFade = Math.max(0, 1 - progress * 2.5)
    iconEl.style.opacity = String(iconFade)
    iconEl.style.transform = `scale(${lerp(1, 0.5, progress)})`
    if (progress > 0.4) {
      iconEl.style.position = 'absolute'
    }
  }

  if (bodyEl) {
    const bodyProgress = Math.max(0, (progress - 0.2) / 0.8)
    bodyEl.style.opacity = String(bodyProgress)
    bodyEl.style.maxHeight = `${lerp(0, 200, bodyProgress)}px`
  }

  const halfIcon = (ICON_SIZE / 2) * scale
  overlay.style.left = `${cursorX - halfIcon - padding * scale}px`
  overlay.style.top = `${cursorY - halfIcon - padding * scale}px`
}

const updateStandardMorph = (ctx: MorphContext) => {
  const {
    container,
    iconEl,
    labelEl,
    bodyEl,
    progress,
    padding,
    scale,
    cursorX,
    cursorY,
    overlay,
    targetWidth
  } = ctx

  if (container) {
    container.style.padding = `${padding}px`
    container.style.borderRadius = `${lerp(6, 12, progress)}px`
    container.style.background = `rgba(255, 255, 255, ${progress})`
    container.style.outline = `rgba(206, 207, 212, ${progress}) 1px solid`
    container.style.boxShadow =
      progress > 0.5
        ? `0 1px 3px 0 rgba(0, 0, 0, ${lerp(0, 0.1, (progress - 0.5) * 2)})`
        : 'none'
    container.style.transform = `scale(${scale})`
    container.style.transformOrigin = 'top left'
    if (targetWidth) {
      container.style.width = `${lerp(ICON_SIZE, targetWidth, progress)}px`
    }
  }

  if (iconEl) {
    iconEl.style.transform = `scale(${lerp(1, 1.125, progress)})`
  }

  if (labelEl) {
    const labelProgress = Math.max(0, (progress - 0.15) / 0.55)
    labelEl.style.opacity = String(Math.min(1, labelProgress))
    labelEl.style.maxWidth = `${lerp(0, 200, Math.min(1, labelProgress))}px`
  }

  if (bodyEl) {
    const bodyProgress = Math.max(0, (progress - 0.5) / 0.5)
    bodyEl.style.opacity = String(bodyProgress)
    bodyEl.style.maxHeight = `${lerp(0, 120, bodyProgress)}px`
  }

  const halfIcon = (ICON_SIZE / 2) * scale
  overlay.style.left = `${cursorX - halfIcon - padding * scale}px`
  overlay.style.top = `${cursorY - halfIcon - padding * scale}px`
}

export const useDragWithMorph = ({
  toolbarRef,
  renderIcon,
  nodeType,
  zoom
}: {
  toolbarRef: RefObject<HTMLDivElement | null>
  renderIcon: () => React.ReactElement
  nodeType: NodeType
  zoom: number
}) => {
  const definitions = useResolvedNodeDefinitions()
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const rootRef = useRef<Root | null>(null)
  const zoomRef = useRef(zoom)
  zoomRef.current = zoom

  // Track cursor position during drag to update the overlay's position,
  // opacity, and morph progress. The overlay morphs from a bare icon
  // (toolbar appearance) to a full node card (canvas appearance) over
  // 150px of upward travel from the toolbar, scaling to match the
  // current canvas zoom level.
  useEffect(() => {
    const isNote = nodeType === 'NOTE'
    const dimensions = TARGET_DIMENSIONS[nodeType]

    const canvasEl = document.querySelector(
      '[data-testid="automations.editor.canvas"]'
    )

    const handleDragOver = (e: DragEvent) => {
      if (!overlayRef.current || !toolbarRef.current) return

      const toolbarRect = toolbarRef.current.getBoundingClientRect()
      const distance = toolbarRect.top - e.clientY
      const progress = Math.min(1, Math.max(0, distance / MORPH_DISTANCE))

      // Clamp cursor position to the canvas bounds so the overlay
      // cannot be dragged outside the editor area.
      let cursorX = e.clientX
      let cursorY = e.clientY
      if (canvasEl) {
        const canvasRect = canvasEl.getBoundingClientRect()
        cursorX = Math.max(canvasRect.left, Math.min(canvasRect.right, cursorX))
        cursorY = Math.max(canvasRect.top, Math.min(canvasRect.bottom, cursorY))
      }

      const overlay = overlayRef.current
      overlay.style.opacity = String(Math.min(1, Math.max(0, distance / 100)))

      const ctx: MorphContext = {
        overlay,
        container: overlay.querySelector('[data-morph-container]'),
        iconEl: overlay.querySelector('[data-morph-icon]'),
        labelEl: overlay.querySelector('[data-morph-label]'),
        bodyEl: overlay.querySelector('[data-morph-body]'),
        progress,
        padding: lerp(0, 12, progress),
        scale: lerp(1, zoomRef.current, progress),
        cursorX,
        cursorY,
        targetWidth: dimensions?.width,
        targetHeight: dimensions?.height
      }

      if (isNote) {
        updateNoteMorph(ctx)
      } else {
        updateStandardMorph(ctx)
      }
    }

    document.addEventListener('dragover', handleDragOver)
    return () => document.removeEventListener('dragover', handleDragOver)
  }, [toolbarRef, nodeType])

  const onDragStart = useCallback(
    (event: React.DragEvent) => {
      // Replace the browser's default drag ghost with a 1×1 transparent
      // image so our custom overlay is the only visible preview.
      const blank = document.createElement('div')
      blank.style.position = 'fixed'
      blank.style.top = '-1px'
      blank.style.left = '-1px'
      blank.style.width = '1px'
      blank.style.height = '1px'
      blank.style.opacity = '0.01'
      document.body.appendChild(blank)
      event.dataTransfer.setDragImage(blank, 0, 0)
      requestAnimationFrame(() => blank.remove())

      // Create a fixed-position overlay that follows the cursor via the
      // dragover handler above. We render a morph preview that transitions
      // from a bare icon to a full node card appearance.
      const overlay = document.createElement('div')
      overlay.style.position = 'fixed'
      overlay.style.pointerEvents = 'none'
      overlay.style.zIndex = '9999'
      overlay.style.opacity = '0'
      document.body.appendChild(overlay)
      overlayRef.current = overlay

      const definition = definitions[nodeType]
      const description = getNodeDescription(
        definition,
        definition.defaultConfig
      )
      const root = createRoot(overlay)
      root.render(
        <DragMorphPreview
          renderIcon={renderIcon}
          nodeType={nodeType}
          name={definition.name}
          description={description}
        />
      )
      rootRef.current = root
    },
    [renderIcon, nodeType, definitions]
  )

  const cleanup = useCallback(() => {
    if (rootRef.current) {
      rootRef.current.unmount()
      rootRef.current = null
    }
    if (overlayRef.current) {
      overlayRef.current.remove()
      overlayRef.current = null
    }
  }, [])

  // Clean up the drag overlay if the component unmounts mid-drag.
  useEffect(() => cleanup, [cleanup])

  return { onDragStart, onDragEnd: cleanup }
}
